import { useEffect, useRef, useState } from "react"
import { useSearchParams, Link } from "react-router-dom"
import { CheckCircle2, Loader2, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  exchangeStripeOAuthCode,
  STRIPE_CONNECT_COMPLETE,
} from "@/lib/stripe-connect"

type CallbackStatus = "exchanging" | "success" | "error"

/**
 * NOS-194 (C2): Stripe Connect OAuth callback — the frontend route
 * /stripe/connect/callback registered in Stripe's Connect OAuth settings.
 *
 * Runs inside the popup opened from the dashboard: receives ?code&state from
 * Stripe, exchanges them server-side via connect-existing-stripe (the CSRF
 * state was generated and stored server-side), then notifies the opener with
 * the existing STRIPE_CONNECT_COMPLETE postMessage and closes itself.
 */
export function StripeConnectCallbackPage() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<CallbackStatus>("exchanging")
  const [error, setError] = useState<string | null>(null)
  const [onboardingComplete, setOnboardingComplete] = useState(true)
  const startedRef = useRef(false)

  const code = searchParams.get("code")
  const state = searchParams.get("state")
  const stripeError = searchParams.get("error")
  const stripeErrorDescription = searchParams.get("error_description")

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    if (stripeError) {
      setStatus("error")
      setError(
        stripeErrorDescription ||
          (stripeError === "access_denied"
            ? "The connection was declined in Stripe."
            : "Stripe returned an error. Please try again.")
      )
      return
    }
    if (!code || !state) {
      setStatus("error")
      setError("Missing connection details from Stripe. Please restart the connection.")
      return
    }

    exchangeStripeOAuthCode(code, state).then(({ data, error: exchangeError }) => {
      if (exchangeError || !data) {
        setStatus("error")
        setError(exchangeError ?? "Could not complete the Stripe connection.")
        return
      }
      setOnboardingComplete(data.onboarding_complete)
      setStatus("success")
      // Mirror openStripeOnboardingPopup's completion contract so the opener
      // refreshes org data whether or not the auto-close below succeeds.
      window.opener?.postMessage({ type: STRIPE_CONNECT_COMPLETE }, window.location.origin)
      setTimeout(() => window.close(), 2000)
    })
  }, [code, state, stripeError, stripeErrorDescription])

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background px-4 text-center">
      {status === "exchanging" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
          <p className="text-muted-foreground">Connecting your Stripe account...</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 className="h-10 w-10 text-green-600" aria-hidden />
          <h1 className="text-lg font-semibold">Stripe account connected</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            {onboardingComplete
              ? "Your existing Stripe account is now connected. You can close this window."
              : "Your Stripe account is connected but not yet fully enabled for charges. Finish any pending steps in your Stripe dashboard, then use Refresh Status."}
          </p>
          <p className="text-xs text-muted-foreground">This window will close automatically.</p>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle className="h-10 w-10 text-destructive" aria-hidden />
          <h1 className="text-lg font-semibold">Connection failed</h1>
          <p className="max-w-md text-sm text-destructive" role="alert">
            {error}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </>
      )}
    </div>
  )
}
